import { useEffect, useRef, useState } from 'react';
import { FiTrash2 } from 'react-icons/fi';

import { useVhAdminStore, VH_ADMIN_PAGE_SIZE, VH_ADMIN_SYSTEM_USER_ID } from '@/admin/admin.store';
import type { VhAdminServiceProvider } from '@/admin/admin.types';
import { formatDate } from '@/admin/admin.users';
import { AdminBadge } from '@/admin/components/AdminBadge';
import { AdminButton } from '@/admin/components/AdminButton';
import { AdminCard, AdminCardList } from '@/admin/components/AdminCard';
import { AdminEntityIdentity } from '@/admin/components/AdminEntityIdentity';
import { AdminForceDeleteModal } from '@/admin/components/AdminForceDeleteModal';
import { AdminPagination } from '@/admin/components/AdminPagination';
import { AdminSearchInput } from '@/admin/components/AdminSearchInput';
import { AdminSpinner } from '@/admin/components/AdminSpinner';
import {
  AdminTable,
  AdminTableCell,
  AdminTableHead,
  AdminTableHeadCell,
  AdminTableRow,
} from '@/admin/components/AdminTable';
import styles from '@/admin/pages/AllServiceProvidersPage.module.scss';
import { isSuperAdminRole, useVhAdminAccessStore } from '@/admin/vh-admin-access.store';
import { toast } from '@/utils/toast';

const typeLabel = (provider: VhAdminServiceProvider) =>
  provider.type === 'marina' ? 'Marina' : 'Service Provider';

const isUserCreated = (provider: VhAdminServiceProvider) =>
  provider.creator_id !== VH_ADMIN_SYSTEM_USER_ID;

const ProviderIdentity = ({ provider }: { provider: VhAdminServiceProvider }) => (
  <AdminEntityIdentity
    name={provider.name}
    image={provider.logo}
    details={provider.email || typeLabel(provider)}
  />
);

const ProviderBadges = ({ provider }: { provider: VhAdminServiceProvider }) => (
  <div className={styles.badges}>
    <AdminBadge>{typeLabel(provider)}</AdminBadge>
    {provider.is_verified && <AdminBadge tone="success">Verified</AdminBadge>}
    {!isUserCreated(provider) && <AdminBadge>Added by VesselHaven</AdminBadge>}
  </div>
);

export const AllServiceProvidersPage = () => {
  const {
    serviceProviders,
    serviceProvidersCount,
    serviceProvidersPage,
    serviceProvidersLoading,
    serviceProviderCreators,
    fetchServiceProviders,
    deleteServiceProvider,
  } = useVhAdminStore();
  const isSuperAdmin = isSuperAdminRole(useVhAdminAccessStore((state) => state.role));
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<VhAdminServiceProvider | null>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const load = () =>
      fetchServiceProviders({ page: 1, search }).catch((err) => {
        console.error(err);
        toast.error('Couldn’t load service providers. Please refresh the page.');
      });

    if (isFirstRender.current) {
      isFirstRender.current = false;
      load();
      return;
    }

    const timeout = setTimeout(load, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const creatorOf = (provider: VhAdminServiceProvider) =>
    serviceProviderCreators[provider.creator_id] ?? provider.creator_id;

  const actions = (provider: VhAdminServiceProvider) =>
    isSuperAdmin && (
      <AdminButton
        size="sm"
        tone="danger"
        onClick={() => setDeleting(provider)}
        aria-label={`Delete ${provider.name}`}
      >
        <FiTrash2 />
        Delete
      </AdminButton>
    );

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.title}>All Service Providers</h1>
        <AdminSearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name or email"
        />
      </div>

      {serviceProvidersLoading && serviceProviders.length === 0 ? (
        <div className={styles.loading}>
          <AdminSpinner />
        </div>
      ) : serviceProviders.length === 0 ? (
        <div className={styles.empty}>
          {search
            ? 'No service providers match. Please try a different search.'
            : 'No service providers yet.'}
        </div>
      ) : (
        <>
          <div className={styles.desktopOnly}>
            <AdminTable>
              <AdminTableHead>
                <AdminTableHeadCell>Service Provider</AdminTableHeadCell>
                <AdminTableHeadCell>Type</AdminTableHeadCell>
                <AdminTableHeadCell>Created By</AdminTableHeadCell>
                <AdminTableHeadCell>Created</AdminTableHeadCell>
                <AdminTableHeadCell>{''}</AdminTableHeadCell>
              </AdminTableHead>
              <tbody>
                {serviceProviders.map((provider) => (
                  <AdminTableRow key={provider.id}>
                    <AdminTableCell>
                      <ProviderIdentity provider={provider} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <ProviderBadges provider={provider} />
                    </AdminTableCell>
                    <AdminTableCell>{creatorOf(provider)}</AdminTableCell>
                    <AdminTableCell>
                      <span className={styles.muted}>{formatDate(provider.created_at)}</span>
                    </AdminTableCell>
                    <AdminTableCell>{actions(provider)}</AdminTableCell>
                  </AdminTableRow>
                ))}
              </tbody>
            </AdminTable>
          </div>

          <div className={styles.mobileOnly}>
            <AdminCardList>
              {serviceProviders.map((provider) => (
                <AdminCard key={provider.id}>
                  <div className={styles.cardHeader}>
                    <ProviderIdentity provider={provider} />
                    {actions(provider)}
                  </div>
                  <ProviderBadges provider={provider} />
                  <div className={styles.cardMeta}>
                    {creatorOf(provider)} · {formatDate(provider.created_at)}
                  </div>
                </AdminCard>
              ))}
            </AdminCardList>
          </div>

          <AdminPagination
            page={serviceProvidersPage}
            pageSize={VH_ADMIN_PAGE_SIZE}
            total={serviceProvidersCount}
            onPageChange={(page) => fetchServiceProviders({ page })}
          />
        </>
      )}

      {deleting && (
        <AdminForceDeleteModal
          kind="service provider"
          name={deleting.name}
          creatorName={serviceProviderCreators[deleting.creator_id] ?? null}
          userCreated={isUserCreated(deleting)}
          consequences="Its locations, photos, and marina details are deleted, and everyone on it loses access and is notified."
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            await deleteServiceProvider(deleting.id);
            toast.success(`“${deleting.name}” has been deleted.`);
          }}
        />
      )}
    </div>
  );
};
